/**
 * API hooks for messages
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { API_ROUTES } from "@/constants/routes";
import { useChatStore } from "@/stores/useChatStore";
import { useAuthStore } from "@/stores/useAuthStore";
import { conversationKeys } from "./useConversations";
import type { ChatResponse, Conversation, CreateMessageRequest, Message } from "@/types/elections";

interface SendMessageParams {
    conversationId: number | 'new';
    content: string;
    candidateId?: number;
}

const sendMessage = async (
    params: SendMessageParams,
    guestSessionId?: string | null
): Promise<ChatResponse> => {
    const payload: CreateMessageRequest = {
        content: params.content,
        candidate_id: params.candidateId,
        guest_session_id: guestSessionId || undefined,
    };

    const url = API_ROUTES.conversationMessages(params.conversationId);
    const { data } = await api.post(url, payload);
    return data;
};

export function useSendMessage() {
    const queryClient = useQueryClient();
    const { isLogged } = useAuthStore();
    const {
        guestSessionId,
        initGuestSession,
        setCurrentConversation,
        clearMessageInput,
        setAiTyping,
    } = useChatStore();

    return useMutation({
        mutationFn: (params: SendMessageParams) => {
            setAiTyping(true);
            const sessionId = isLogged ? null : (guestSessionId || initGuestSession());
            return sendMessage(params, sessionId);
        },
        onSuccess: (data) => {
            setCurrentConversation(data.conversation_id);

            // Write messages into cache synchronously — eliminates flicker
            queryClient.setQueryData(
                conversationKeys.detail(data.conversation_id),
                (old: Conversation | undefined) => {
                    if (!old) {
                        return {
                            id: data.conversation_id,
                            title: data.user_message.content.slice(0, 60),
                            is_archived: false,
                            last_message_at: data.ai_message.created,
                            messages: [data.user_message, data.ai_message],
                        };
                    }
                    return {
                        ...old,
                        last_message_at: data.ai_message.created,
                        messages: [...(old.messages ?? []), data.user_message, data.ai_message],
                    };
                }
            );

            queryClient.invalidateQueries({ queryKey: conversationKeys.detail(data.conversation_id) });
            queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });

            clearMessageInput();
            setAiTyping(false);
        },
        onError: () => {
            setAiTyping(false);
        },
    });
}

// Helper to add optimistic messages to cache
export function useOptimisticMessage() {
    // Reserved for future optimistic update implementation
    const addOptimisticMessage = (_conversationId: number, message: Partial<Message>) => {
        const optimisticMessage: Message = {
            id: Date.now(), // Temporary ID
            role: 'user',
            content: message.content || '',
            sources: [],
            created: new Date().toISOString(),
            ...message,
        };

        // Could implement optimistic updates here
        // For now, we'll rely on invalidation
        return optimisticMessage;
    };

    return { addOptimisticMessage };
}
